import { z } from "zod"

/**
 * Das AV-Tool: aus den Angaben des Kunden eine Versorgungslücke rechnen.
 *
 * Pure und synchron, weil es zweimal läuft — bei jedem Tastendruck im Browser
 * und noch einmal auf dem Server, der dem Browser die Zahlen nicht glaubt.
 *
 * VIER KONVENTIONEN, an denen jede Formel hier unten hängt:
 *
 * K1 — Zeitbezug. Jeder Eurobetrag ist NOMINAL zum Rentenbeginn, mit genau
 *      einer Ausnahme: die Wunschrente steht in HEUTIGER Kaufkraft, weil der
 *      Kunde sich am heutigen Netto orientiert. Daraus folgt die Identität
 *      `lueckeNominal === lueckeHeute * (1 + i) ** n`, die ein Test festhält.
 *      Eine Hochrechnung mit 6 % und eine Zahl aus der Renteninformation
 *      stehen sonst in verschiedenen Währungen und dürfen nicht addiert werden
 *      — dafür trägt jeder Vertrag ein `wertbezug`.
 *
 * K2 — Keine Uhr. Nie `Date.now()` oder `new Date()`. Der Aufrufer übergibt
 *      `basisjahr`. Sonst hinge das Ergebnis am Tag des Testlaufs.
 *
 * K3 — Tarif auf Rädern. Grundfreibetrag und Tarifzonen werden vor der
 *      Rentenbesteuerung mit der Inflation fortgeschrieben. Ohne das wird eine
 *      Rente in 27 Jahren gegen den Tarif von heute versteuert und die
 *      Nettorente fällt grotesk zu niedrig aus. FOLGE: Block 9 hängt an der
 *      Eingabe von Block 10 — die Inflationsannahme ändert die Nettorente,
 *      nicht nur die Kaufkraftzeile.
 *
 * K4 — Rundung. Intern ungerundet, nur Geldfelder der Ausgabe auf Cent. Nicht
 *      auf ganze Euro wie `personalPersonaFall`, sonst reißt die K1-Identität.
 *
 * ZWEI MITTEL FÜR ZWEI VERSCHIEDENE PROBLEME, die nicht zu vereinheitlichen
 * sind: `z.discriminatedUnion` steht dort, wo es zwei gleichberechtigte Wege
 * zur selben Aussage gibt (Brutto ODER Netto, bekannte Rente ODER
 * Hochrechnung). `ueberschreibung?: number` steht dort, wo ein berechneter Wert
 * von Hand übergangen werden darf und der berechnete daneben sichtbar bleiben
 * muss (gesetzliche Rente, Pflichtversorgung).
 */

const enumFrom = <T extends string>(
  entries: readonly { readonly value: T }[]
) => z.enum(entries.map((entry) => entry.value) as [T, ...T[]])

/* ------------------------------------------------------------------ *
 * Auswahllisten
 * ------------------------------------------------------------------ */

/**
 * Block 1. „Beamter" und „öffentlicher Dienst" sind getrennt, obwohl die
 * Vorgabe sie in einem Atemzug nennt: Block 7 muss für den einen eine
 * Beamtenpension und für den anderen eine ZVK-Rente liefern, Block 6 für den
 * einen null und für den anderen die volle gesetzliche Rente. Zusammengelegt
 * sind beide Blöcke nicht beantwortbar.
 */
export const SOZIALVERSICHERUNGS_STATUS = [
  { value: "arbeitnehmer", label: "Arbeitnehmer" },
  { value: "selbststaendig", label: "Selbstständig" },
  { value: "beamter", label: "Beamter" },
  { value: "oeffentlicher_dienst", label: "Öffentlicher Dienst (tariflich)" },
] as const
export type SozialversicherungsStatus =
  (typeof SOZIALVERSICHERUNGS_STATUS)[number]["value"]

export const KRANKENVERSICHERUNGS_ARTEN = [
  { value: "gkv", label: "Gesetzlich (GKV)" },
  { value: "pkv", label: "Privat (PKV)" },
] as const

/**
 * Block 3. „Ja/nein" allein reicht nicht — 8 % gegen 9 % hängt am Bundesland.
 * Der Satz wird deshalb mitgeführt, statt ihn zu raten.
 */
export const KIRCHENSTEUER_SAETZE = [
  { value: "acht", label: "8 % (Bayern, Baden-Württemberg)" },
  { value: "neun", label: "9 % (übrige Bundesländer)" },
] as const

export const STEUERKLASSEN = [
  { value: "i", label: "Steuerklasse I" },
  { value: "ii", label: "Steuerklasse II" },
  { value: "iii", label: "Steuerklasse III" },
  { value: "iv", label: "Steuerklasse IV" },
  { value: "v", label: "Steuerklasse V" },
  { value: "vi", label: "Steuerklasse VI" },
] as const
export type Steuerklasse = (typeof STEUERKLASSEN)[number]["value"]

export const SCHICHTEN = [
  { value: "erste", label: "1. Schicht (Basisrente/Rürup)" },
  { value: "zweite", label: "2. Schicht (bAV, Riester)" },
  { value: "dritte", label: "3. Schicht (privat)" },
] as const
export type Schicht = (typeof SCHICHTEN)[number]["value"]

export const VERTRAGS_TYPEN = [
  { value: "bav_direktversicherung", label: "bAV: Direktversicherung" },
  { value: "bav_pensionskasse", label: "bAV: Pensionskasse" },
  { value: "bav_direktzusage", label: "bAV: Direktzusage/U-Kasse" },
  { value: "riester", label: "Riester-Rente" },
  { value: "ruerup", label: "Rürup-/Basisrente" },
  { value: "private_rv", label: "Private Rentenversicherung" },
  { value: "fondspolice", label: "Fondsgebundene Rentenversicherung" },
  { value: "fondssparplan", label: "Fondssparplan" },
] as const
export type VertragsTyp = (typeof VERTRAGS_TYPEN)[number]["value"]

/** K1: in welcher Währung eine eingetragene Rente steht. */
export const WERTBEZUG = [
  { value: "nominal", label: "nominal zu Rentenbeginn" },
  { value: "heute", label: "in heutiger Kaufkraft" },
] as const

/**
 * Schicht, Besteuerung und Beitragspflicht folgen aus dem Vertragstyp.
 *
 * Abgeleitet und nicht eingegeben: ein eigenes Schicht-Feld lüde zum
 * Widerspruch ein („Riester in der 3. Schicht"), den die Rechnung dann
 * schlichten müsste. Nebenbei findet so auch die 1. Schicht Platz, die die
 * Vorgabe nicht nennt, die aber das Hauptprodukt einer Selbstständigen ist.
 */
export const VERTRAGS_BEHANDLUNG: Record<
  VertragsTyp,
  {
    schicht: Schicht
    besteuerung: "voll" | "ertragsanteil" | "kohorte"
    kvPflichtig: boolean
  }
> = {
  bav_direktversicherung: {
    schicht: "zweite",
    besteuerung: "voll",
    kvPflichtig: true,
  },
  bav_pensionskasse: {
    schicht: "zweite",
    besteuerung: "voll",
    kvPflichtig: true,
  },
  bav_direktzusage: {
    schicht: "zweite",
    besteuerung: "voll",
    kvPflichtig: true,
  },
  riester: { schicht: "zweite", besteuerung: "voll", kvPflichtig: false },
  ruerup: { schicht: "erste", besteuerung: "kohorte", kvPflichtig: false },
  private_rv: {
    schicht: "dritte",
    besteuerung: "ertragsanteil",
    kvPflichtig: false,
  },
  fondspolice: {
    schicht: "dritte",
    besteuerung: "ertragsanteil",
    kvPflichtig: false,
  },
  fondssparplan: {
    schicht: "dritte",
    besteuerung: "ertragsanteil",
    kvPflichtig: false,
  },
}

/* ------------------------------------------------------------------ *
 * Der Annahmensatz
 * ------------------------------------------------------------------ */

/**
 * Die Annahmen hinter jeder Zahl, die dieses Werkzeug zeigt.
 *
 * Versioniert und exportiert mit Absicht: die Beraterin sieht `stand` im Fuß
 * des Werkzeugs, und ein Kunde, der fragt „woher kommt das", muss eine Antwort
 * bekommen. Wer hier einen Wert ändert, ändert, was das Trainingswerkzeug
 * behauptet — das ist eine fachliche Entscheidung, kein Refactoring, und sie
 * erhöht `version`. Still geänderte Zahlen ließen gespeicherte Fälle
 * rückwirkend anders rechnen.
 *
 * BEWUSST NICHT MODELLIERT: Solidaritätszuschlag (liegt für jedes Einkommen,
 * das dieses Werkzeug sieht, unter der Freigrenze, und die Milderungszone
 * bräuchte den vollen Tarif), Versorgungsfreibetrag für Pensionen (läuft 2058
 * aus), Riester-Zulagen und Günstigerprüfung (das Werkzeug zeigt die
 * Anwartschaft, nicht die Förderung), Ost/West (seit 2025 bundeseinheitlich),
 * Altersfaktor und Startgutschriften im VBL-Punktemodell.
 */
export const AV_ANNAHMEN = {
  stand: "2026-01",
  version: 1,
  basisjahr: 2026,

  // --- Sozialversicherung in der Erwerbsphase ---
  /** § 158 SGB VI, Arbeitnehmer- und Arbeitgeberanteil zusammen. */
  rentenversicherungSatz: 0.186,
  /** § 341 SGB III. */
  arbeitslosenversicherungSatz: 0.026,
  /** § 241 SGB V, allgemeiner Beitragssatz. */
  krankenversicherungSatz: 0.146,
  /** § 242a SGB V, durchschnittlicher Zusatzbeitrag. */
  zusatzbeitragStandard: 0.029,
  /** § 55 SGB XI, allgemeiner Beitragssatz. */
  pflegeversicherungSatz: 0.036,
  /** § 55 Abs. 3 SGB XI, Zuschlag für Kinderlose, allein vom Versicherten. */
  pflegeZuschlagKinderlose: 0.006,
  /** Beitragsbemessungsgrenze RV/AV, Jahreswert. */
  bbgRentenversicherung: 101_400,
  /** Beitragsbemessungsgrenze KV/PV, Jahreswert. */
  bbgKrankenversicherung: 69_750,

  // --- Steuer ---
  /** § 32a Abs. 1 EStG. */
  grundfreibetrag: 12_348,
  /** § 9a Nr. 1a EStG. */
  arbeitnehmerPauschbetrag: 1_230,
  /** § 10c EStG. */
  sonderausgabenPauschbetrag: 36,
  /** § 9a Nr. 3 EStG, bei sonstigen Einkünften (Renten). */
  werbungskostenPauschbetragRente: 102,
  /** § 24b EStG, Steuerklasse II. */
  entlastungAlleinerziehende: 4_260,
  /** Kirchensteuer: 8 % in BY und BW, 9 % in den übrigen Ländern. */
  kirchensteuerAcht: 0.08,
  kirchensteuerNeun: 0.09,

  // --- Gesetzliche Rente ---
  /** § 68 SGB VI, aktueller Rentenwert, bundeseinheitlich. */
  rentenwert: 40.79,
  /** Anlage 1 SGB VI, vorläufiges Durchschnittsentgelt. */
  durchschnittsentgelt: 51_944,
  /** § 77 SGB VI, Abschlag je Monat vorgezogenen Rentenbeginns. */
  abschlagProMonat: 0.003,
  /** § 77 SGB VI, Zuschlag je Monat aufgeschobenen Rentenbeginns. */
  zuschlagProMonat: 0.005,
  /** § 22 Nr. 1 EStG: Besteuerungsanteil der Kohorte 2023 und Steigung. */
  besteuerungsanteilBasis: 0.825,
  besteuerungsanteilBasisJahr: 2023,
  besteuerungsanteilSteigungProJahr: 0.005,

  // --- Krankenversicherung in der Rentenphase ---
  /** § 226 Abs. 2 SGB V, Freibetrag auf Versorgungsbezüge, monatlich. */
  bavFreibetragMonatlich: 196,
  /** § 106 SGB VI, Zuschuss zur PKV: halber allgemeiner Beitragssatz. */
  pkvZuschussSatz: 0.073,

  // --- Zusatz- und Pflichtversorgung ---
  /**
   * Zusatzversorgung im öffentlichen Dienst je Pflichtversicherungsjahr, als
   * Anteil des Bruttomonatsentgelts. Verdichtung des VBL-Punktemodells auf
   * einen linearen Satz bei Altersfaktor 1,0; unterschätzt junge Eintritte —
   * für ein Trainingswerkzeug bewusst die vorsichtige Richtung.
   */
  zvkSatzProJahr: 0.004,
  /** § 14 BeamtVG, Ruhegehaltssatz je ruhegehaltfähigem Dienstjahr. */
  ruhegehaltssatzProJahr: 0.0179375,
  /** § 14 BeamtVG, Höchstruhegehaltssatz. */
  ruhegehaltssatzMax: 0.7175,
  /** § 14 Abs. 3 BeamtVG, Versorgungsabschlag je Jahr und Deckel. */
  versorgungsabschlagProJahr: 0.036,
  versorgungsabschlagMax: 0.144,

  // --- Kapital ---
  /**
   * Monatliche Rente je 10.000 € Kapital. So gewählt, dass sie den
   * 350er-Faktor aus `personalPersonaFall` reproduziert (10.000/350 = 28,57):
   * AV-Tool und ausgelieferte Trainingsfälle sollen nicht auseinanderlaufen.
   */
  rentenfaktorJe10000: 28.571_428_571_428_573,
  zinsStandardProzent: 3,

  // --- Inflation ---
  /** Handout 4 „Bedarfsanalyse + Lösung" S. 5: das System rechnet mit 2 %. */
  inflationStandardProzent: 2,
  /** K3. */
  tarifAufRaedern: true,
} as const

/**
 * Stückweise lineare Grenzsatz-Näherung des Tarifs nach § 32a EStG.
 *
 * Der echte Tarif ist stückweise quadratisch. Die Näherung behält seine FORM —
 * innerhalb einer Zone steigt der Grenzsatz linear — und leitet die
 * Koeffizienten aus den Zonengrenzen ab statt aus den veröffentlichten
 * Faktoren. Damit ist sie an jeder Grenze stetig und per Konstruktion monoton.
 * Der volle § 32a ist bewusst nicht Gegenstand.
 */
export const STEUER_ZONEN = [
  { bis: 12_348, grenzsatzVon: 0, grenzsatzBis: 0 },
  { bis: 17_443, grenzsatzVon: 0.14, grenzsatzBis: 0.24 },
  { bis: 68_480, grenzsatzVon: 0.24, grenzsatzBis: 0.42 },
  { bis: 277_825, grenzsatzVon: 0.42, grenzsatzBis: 0.42 },
  { bis: Number.POSITIVE_INFINITY, grenzsatzVon: 0.45, grenzsatzBis: 0.45 },
] as const

/** Tarifparameter je Lohnsteuerklasse. Keine zweite Tariffunktion. */
export const STEUERKLASSEN_PARAMETER: Record<
  Steuerklasse,
  {
    splitting: number
    grundfreibetrag: boolean
    pauschbetraege: boolean
    alleinerziehend: boolean
  }
> = {
  i: {
    splitting: 1,
    grundfreibetrag: true,
    pauschbetraege: true,
    alleinerziehend: false,
  },
  ii: {
    splitting: 1,
    grundfreibetrag: true,
    pauschbetraege: true,
    alleinerziehend: true,
  },
  iii: {
    splitting: 2,
    grundfreibetrag: true,
    pauschbetraege: true,
    alleinerziehend: false,
  },
  iv: {
    splitting: 1,
    grundfreibetrag: true,
    pauschbetraege: true,
    alleinerziehend: false,
  },
  v: {
    splitting: 1,
    grundfreibetrag: false,
    pauschbetraege: true,
    alleinerziehend: false,
  },
  vi: {
    splitting: 1,
    grundfreibetrag: false,
    pauschbetraege: false,
    alleinerziehend: false,
  },
}

/** Ertragsanteil einer Leibrente, § 22 Nr. 1 S. 3 a) bb) EStG, Auszug. */
export const ERTRAGSANTEIL_TABELLE: readonly {
  abAlter: number
  anteil: number
}[] = [
  { abAlter: 55, anteil: 0.26 },
  { abAlter: 57, anteil: 0.24 },
  { abAlter: 60, anteil: 0.22 },
  { abAlter: 62, anteil: 0.21 },
  { abAlter: 63, anteil: 0.2 },
  { abAlter: 65, anteil: 0.18 },
  { abAlter: 67, anteil: 0.17 },
  { abAlter: 70, anteil: 0.15 },
]

/**
 * Der Hinweis unter dem Werkzeug.
 *
 * Ohne den Annahmenstand: der Berater kann mit einem Datum nichts anfangen,
 * und im Gespräch ist die Zeile ohnehin nur der Vorbehalt. Nachvollziehbar
 * bleibt der Stand trotzdem — er reist in `avTool.annahmenStand` mit jedem
 * gespeicherten Ergebnis und steht so auch im Feedback-Prompt.
 */
export const AV_TOOL_HINWEIS =
  "Überschlagsrechnung für das Training. Die Werte beruhen auf vereinfachten " +
  "Annahmen und sind kein Beratungsergebnis."

/* ------------------------------------------------------------------ *
 * Eingabe
 * ------------------------------------------------------------------ */

export const sozialversicherungsStatusSchema = enumFrom(
  SOZIALVERSICHERUNGS_STATUS
)
export const krankenversicherungsArtSchema = enumFrom(
  KRANKENVERSICHERUNGS_ARTEN
)
export const kirchensteuerSatzSchema = enumFrom(KIRCHENSTEUER_SAETZE)
export const steuerklasseSchema = enumFrom(STEUERKLASSEN)
export const schichtSchema = enumFrom(SCHICHTEN)
export const vertragsTypSchema = enumFrom(VERTRAGS_TYPEN)
export const wertbezugSchema = enumFrom(WERTBEZUG)

const geld = (max = 50_000) =>
  z
    .number({ error: "Bitte einen Betrag eingeben." })
    .min(0, "Der Betrag darf nicht negativ sein.")
    .max(max, `Bitte höchstens ${max.toLocaleString("de-DE")} € eingeben.`)

const prozent = (max: number, was: string) =>
  z
    .number({ error: `Bitte ${was} eingeben.` })
    .min(0, "Der Wert darf nicht negativ sein.")
    .max(max, `Bitte höchstens ${max} % eingeben.`)

/** Block 5: ein bestehender Vertrag. */
export const avVertragSchema = z.object({
  /** Stabiler Schlüssel der Zeile. Ein Index verrutschte beim Löschen. */
  id: z.string().min(1),
  bezeichnung: z.string().trim().max(120).optional(),
  typ: vertragsTypSchema,
  /**
   * Zwei gleichwertige Wege zur selben Aussage, nicht Wert und Korrektur:
   * entweder steht die Rente in den Unterlagen, oder sie wird aus Beitrag,
   * Laufzeit und Zins hochgerechnet — der Fall „auch ohne Vertragsunterlagen".
   */
  angabe: z.discriminatedUnion("art", [
    z.object({
      art: z.literal("rente_bekannt"),
      monatsrente: geld(20_000),
      wertbezug: wertbezugSchema.default("nominal"),
    }),
    z.object({
      art: z.literal("hochrechnung"),
      beitragMonatlich: geld(10_000),
      /** Rückkaufswert bzw. bereits vorhandenes Kapital. */
      guthaben: geld(2_000_000).default(0),
      /** Leer = bis zum Wunscheintrittsalter. */
      laufzeitJahre: z
        .number()
        .int("Bitte volle Jahre angeben.")
        .min(0, "Die Laufzeit darf nicht negativ sein.")
        .max(60, "Bitte höchstens 60 Jahre Laufzeit angeben.")
        .optional(),
      zinsProzent: prozent(12, "eine Verzinsung").default(
        AV_ANNAHMEN.zinsStandardProzent
      ),
    }),
  ]),
  /** Nur bAV: Arbeitgeberfinanzierung. Darstellung, ohne Rechenwirkung. */
  arbeitgeberFinanziert: z.boolean().default(false),
})
export type AvVertrag = z.infer<typeof avVertragSchema>

/**
 * Die Eingabe ohne die Querprüfungen.
 *
 * Getrennt exportiert, weil der Fallzustand sie speichert: eine später
 * verschärfte `superRefine` darf einen gespeicherten Stand nicht rückwirkend
 * ungültig machen. Formulare prüfen gegen `avToolEingabeSchema`, die
 * Persistenz gegen diese Basis.
 */
export const avToolEingabeBasisSchema = z.object({
  /** K2: das Bezugsjahr kommt von außen, das Modul liest keine Uhr. */
  basisjahr: z
    .number()
    .int()
    .min(2020)
    .max(2100)
    .default(AV_ANNAHMEN.basisjahr),
  alter: z
    .number({ error: "Bitte das Alter eingeben." })
    .int("Bitte das Alter in vollen Jahren angeben.")
    .min(18, "Mindestens 18 Jahre.")
    .max(70, "Höchstens 70 Jahre."),
  /** Steuert allein den Zuschlag für Kinderlose in der Pflegeversicherung. */
  hatKinder: z.boolean().default(false),

  // 1 — Sozialversicherungsstatus
  status: sozialversicherungsStatusSchema,

  // 2 — Krankenversicherungsstatus
  krankenversicherung: z.discriminatedUnion("art", [
    z.object({
      art: z.literal("gkv"),
      zusatzbeitragProzent: prozent(5, "einen Zusatzbeitrag").default(
        AV_ANNAHMEN.zusatzbeitragStandard * 100
      ),
    }),
    z.object({
      art: z.literal("pkv"),
      beitragMonatlich: geld(3_000),
      /** Leer = der heutige Beitrag wird fortgeschrieben, mit Hinweis. */
      beitragImRuhestandMonatlich: geld(3_000).optional(),
    }),
  ]),

  // 3 — Kirchensteuer
  kirchensteuer: z.discriminatedUnion("pflichtig", [
    z.object({ pflichtig: z.literal(false) }),
    z.object({ pflichtig: z.literal(true), satz: kirchensteuerSatzSchema }),
  ]),

  // 4 — Einkommen
  einkommen: z.discriminatedUnion("art", [
    z.object({ art: z.literal("brutto"), bruttoMonatlich: geld(50_000) }),
    z.object({ art: z.literal("netto"), nettoMonatlich: geld(30_000) }),
  ]),
  /** Nur Arbeitnehmer, Beamte und öffentlicher Dienst haben eine Klasse. */
  steuerklasse: steuerklasseSchema.optional(),

  // 5 — Bestehende Altersvorsorgeverträge
  vertraege: z
    .array(avVertragSchema)
    .max(20, "Bitte höchstens 20 Verträge erfassen.")
    .default([]),

  // 6 — Gesetzliche Rente
  gesetzlicheRente: z
    .object({
      beitragsjahreBisher: z
        .number()
        .int("Bitte volle Beitragsjahre angeben.")
        .min(0, "Die Beitragsjahre dürfen nicht negativ sein.")
        .max(55, "Bitte höchstens 55 Beitragsjahre angeben.")
        .default(0),
      /** Aus der Renteninformation, schlägt die Schätzung. */
      entgeltpunkteBisher: z
        .number()
        .min(0, "Entgeltpunkte dürfen nicht negativ sein.")
        .max(100, "Bitte höchstens 100 Entgeltpunkte angeben.")
        .optional(),
      ueberschreibung: geld(20_000).optional(),
    })
    .default({ beitragsjahreBisher: 0 }),

  // 7 — Zusatzversorgungskassen / Pflichtversorgungssysteme
  pflichtversorgung: z
    .object({
      /** Pflichtversicherungsjahre (ZVK) bzw. ruhegehaltfähige Dienstjahre. */
      jahre: z
        .number()
        .int("Bitte volle Jahre angeben.")
        .min(0, "Die Jahre dürfen nicht negativ sein.")
        .max(55, "Bitte höchstens 55 Jahre angeben.")
        .default(0),
      ueberschreibung: geld(20_000).optional(),
    })
    .default({ jahre: 0 }),

  // 8 — Wunschrente inklusive Wunscheintrittsalter
  wunschEintrittsalter: z
    .number({ error: "Bitte ein Wunscheintrittsalter eingeben." })
    .int("Bitte volle Jahre angeben.")
    .min(55, "Frühestens mit 55 Jahren.")
    .max(75, "Spätestens mit 75 Jahren."),
  wunschrente: z.discriminatedUnion("art", [
    z.object({ art: z.literal("betrag"), betragMonatlich: geld(30_000) }),
    z.object({
      art: z.literal("prozentVomNetto"),
      prozent: z
        .number({ error: "Bitte einen Prozentsatz eingeben." })
        .min(10, "Mindestens 10 % des heutigen Nettos.")
        .max(200, "Höchstens 200 % des heutigen Nettos."),
    }),
  ]),

  // 9 — Weitere Abzüge in der Rentenphase
  sonstigeAbzuegeMonatlich: geld(5_000).default(0),

  // 10 — Inflation und Kaufkraftentwicklung
  inflationProzent: prozent(10, "eine Inflationsannahme").default(
    AV_ANNAHMEN.inflationStandardProzent
  ),
})

export const avToolEingabeSchema = avToolEingabeBasisSchema.superRefine(
  (value, ctx) => {
    if (value.wunschEintrittsalter <= value.alter)
      ctx.addIssue({
        code: "custom",
        path: ["wunschEintrittsalter"],
        message: "Der Rentenbeginn muss nach dem aktuellen Alter liegen.",
      })
    // Lohnsteuerklassen gibt es nur für Arbeitnehmer und Beamte. Für
    // Selbstständige ist „Bruttoeinkommen" ausserdem der Gewinn, nicht der
    // Bruttolohn — das muss die Beschriftung in der Oberfläche mittragen.
    const lohnsteuerpflichtig = value.status !== "selbststaendig"
    if (lohnsteuerpflichtig && !value.steuerklasse)
      ctx.addIssue({
        code: "custom",
        path: ["steuerklasse"],
        message: "Bitte die Steuerklasse angeben.",
      })
    if (!lohnsteuerpflichtig && value.steuerklasse)
      ctx.addIssue({
        code: "custom",
        path: ["steuerklasse"],
        message: "Für Selbstständige gibt es keine Lohnsteuerklasse.",
      })
    if (value.gesetzlicheRente.beitragsjahreBisher > value.alter - 15)
      ctx.addIssue({
        code: "custom",
        path: ["gesetzlicheRente", "beitragsjahreBisher"],
        message: "So viele Beitragsjahre sind in diesem Alter nicht möglich.",
      })
    if (value.pflichtversorgung.jahre > value.alter - 15)
      ctx.addIssue({
        code: "custom",
        path: ["pflichtversorgung", "jahre"],
        message: "So viele Jahre sind in diesem Alter nicht möglich.",
      })
    const punkte = value.gesetzlicheRente.entgeltpunkteBisher
    if (punkte !== undefined && punkte > Math.max(0, value.alter - 15) * 2)
      ctx.addIssue({
        code: "custom",
        path: ["gesetzlicheRente", "entgeltpunkteBisher"],
        message: "Höchstens zwei Entgeltpunkte je Beitragsjahr.",
      })
  }
)
export type AvToolEingabe = z.infer<typeof avToolEingabeSchema>

/* ------------------------------------------------------------------ *
 * Ergebnis
 * ------------------------------------------------------------------ */

/**
 * Ein Wert, den das Werkzeug rechnet und die Beraterin übergehen darf.
 *
 * Alle vier Felder stehen im ERGEBNIS; die Eingabe trägt nur
 * `ueberschreibung`. Damit bleibt der berechnete Wert eine Ableitung und kann
 * nicht als veralteter Rechenstand in gespeicherten Daten überdauern, wenn
 * `AV_ANNAHMEN` fortgeschrieben wird — und die Oberfläche kann trotzdem beide
 * Zahlen nebeneinander zeigen („errechnet 1.696 €, laut Renteninformation
 * 1.540 €"), was eine Union im Eingabeschema unmöglich machte.
 */
export const ueberschriebenerWertSchema = z.object({
  berechnet: z.number(),
  manuell: z.number().optional(),
  /** Der Wert, mit dem weitergerechnet wurde. */
  wert: z.number(),
  quelle: z.enum(["berechnet", "manuell"]),
})
export type UeberschriebenerWert = z.infer<typeof ueberschriebenerWertSchema>

export const avVertragErgebnisSchema = z.object({
  id: z.string(),
  typ: vertragsTypSchema,
  schicht: schichtSchema,
  hochgerechnet: z.boolean(),
  kapitalZuRentenbeginn: z.number().optional(),
  /** Monatliche Bruttorente, nominal zum Rentenbeginn (K1). */
  bruttoRenteMonatlich: z.number(),
  steuerpflichtigerAnteilMonatlich: z.number(),
  kvPflichtigerAnteilMonatlich: z.number(),
})
export type AvVertragErgebnis = z.infer<typeof avVertragErgebnisSchema>

export const avToolErgebnisSchema = z.object({
  annahmenStand: z.string(),
  annahmenVersion: z.number().int(),
  hinweis: z.string(),
  /** Weiche Warnungen auf Deutsch — kein Validierungsfehler, siehe unten. */
  hinweise: z.array(z.string()),

  zeit: z.object({
    alter: z.number().int(),
    wunschEintrittsalter: z.number().int(),
    geburtsjahr: z.number().int(),
    rentenjahr: z.number().int(),
    jahreBisRente: z.number().int(),
    regelaltersgrenze: z.number(),
    /** < 1 bei vorgezogenem, > 1 bei aufgeschobenem Rentenbeginn. */
    zugangsfaktor: z.number(),
  }),

  // Block 4
  einkommen: z.object({
    bruttoMonatlich: z.number(),
    nettoMonatlich: z.number(),
    hochgerechnet: z.boolean(),
    abzuege: z.object({
      rentenversicherung: z.number(),
      arbeitslosenversicherung: z.number(),
      krankenversicherung: z.number(),
      pflegeversicherung: z.number(),
      lohnsteuer: z.number(),
      kirchensteuer: z.number(),
      summe: z.number(),
    }),
  }),

  // Block 6
  gesetzlicheRente: z.object({
    bruttoMonatlich: ueberschriebenerWertSchema,
    entgeltpunkteBisher: z.number(),
    entgeltpunkteKuenftig: z.number(),
    entgeltpunkteGesamt: z.number(),
  }),

  // Block 7
  pflichtversorgung: z.object({
    /** Für Beamte ist die Pension die HAUPTversorgung, keine Zusatzversorgung
     *  — die Überschrift in der Oberfläche wechselt mit diesem Feld. */
    art: z.enum(["zvk", "beamtenpension", "keine"]),
    bruttoMonatlich: ueberschriebenerWertSchema,
    ruhegehaltssatz: z.number().optional(),
    versorgungsabschlag: z.number().optional(),
  }),

  // Block 5
  vertraege: z.array(avVertragErgebnisSchema),
  schichten: z.object({
    ersteSchichtMonatlich: z.number(),
    zweiteSchichtMonatlich: z.number(),
    dritteSchichtMonatlich: z.number(),
  }),

  // Block 9
  bruttoRenteGesamtMonatlich: z.number(),
  renteAbzuege: z.object({
    krankenversicherung: z.number(),
    pflegeversicherung: z.number(),
    /** PKV: fester Beitrag statt Satz. */
    pkvBeitrag: z.number(),
    /** § 106 SGB VI, mindert den PKV-Beitrag. */
    pkvZuschuss: z.number(),
    einkommensteuer: z.number(),
    kirchensteuer: z.number(),
    sonstige: z.number(),
    summe: z.number(),
  }),
  renteSteuer: z.object({
    besteuerungsanteilGesetzlich: z.number(),
    ertragsanteilPrivat: z.number(),
    steuerpflichtigeRenteMonatlich: z.number(),
    abziehbareVorsorgeaufwendungenJahr: z.number(),
    zuVersteuerndesEinkommenJahr: z.number(),
    /** K3: mit der Inflation fortgeschrieben. */
    grundfreibetragImRuhestand: z.number(),
  }),
  nettoRenteMonatlich: z.number(),

  // Blöcke 8 und 10
  /** K1: in heutiger Kaufkraft. */
  wunschrenteMonatlich: z.number(),
  wunschrenteNominalMonatlich: z.number(),
  inflationProzent: z.number(),
  kaufkraftfaktor: z.number(),
  nettoRenteKaufkraftMonatlich: z.number(),
  kaufkraftverlustMonatlich: z.number(),
  versorgungsluecke: z.object({
    heuteMonatlich: z.number(),
    nominalMonatlich: z.number(),
    ueberschussHeuteMonatlich: z.number(),
    deckungsgrad: z.number(),
  }),
})
export type AvToolErgebnis = z.infer<typeof avToolErgebnisSchema>

/* ------------------------------------------------------------------ *
 * Rechnung
 * ------------------------------------------------------------------ */

/** K4: nur Ausgabewerte runden, und zwar auf Cent. */
const cent = (n: number) => Math.round(n * 100) / 100

/**
 * Einkommensteuer nach der Zonen-Näherung.
 *
 * In einer Zone der Breite `w`, deren Grenzsatz linear von `m0` auf `m1`
 * steigt, ist die Steuer auf eine Scheibe `s` das Integral des Grenzsatzes:
 * `m0*s + (m1-m0)*s²/(2w)`. An jeder Zonengrenze stetig, monoton per
 * Konstruktion.
 *
 * `tarifFaktor` schiebt alle Grenzen mit — das ist K3.
 */
export function einkommensteuerJahr(zvE: number, tarifFaktor = 1): number {
  if (!(zvE > 0)) return 0
  let steuer = 0
  let untergrenze = 0
  for (const zone of STEUER_ZONEN) {
    const obergrenze = zone.bis * tarifFaktor
    const breite = obergrenze - untergrenze
    const scheibe = Math.min(zvE, obergrenze) - untergrenze
    if (scheibe <= 0) break
    steuer += Number.isFinite(breite)
      ? zone.grenzsatzVon * scheibe +
        ((zone.grenzsatzBis - zone.grenzsatzVon) * scheibe * scheibe) /
          (2 * breite)
      : zone.grenzsatzVon * scheibe
    if (zvE <= obergrenze) break
    untergrenze = obergrenze
  }
  return steuer
}

/**
 * Lohnsteuer als Parametrisierung desselben Tarifs, nicht als zweite Formel.
 *
 * Klasse V und VI werden dadurch abgebildet, dass der Grundfreibetrag vorab
 * verbraucht wird. Das trifft ihren beherrschenden Effekt — kein steuerfreier
 * Betrag — ohne einen zweiten Tarif zu pflegen.
 */
export function lohnsteuerJahr(
  zvE: number,
  klasse: Steuerklasse | undefined,
  tarifFaktor = 1
): number {
  if (!klasse) return einkommensteuerJahr(zvE, tarifFaktor)
  const p = STEUERKLASSEN_PARAMETER[klasse]
  let basis = zvE
  if (p.alleinerziehend)
    basis -= AV_ANNAHMEN.entlastungAlleinerziehende * tarifFaktor
  if (!p.grundfreibetrag) basis += AV_ANNAHMEN.grundfreibetrag * tarifFaktor
  if (!(basis > 0)) return 0
  return p.splitting * einkommensteuerJahr(basis / p.splitting, tarifFaktor)
}

type SvEingabe = Pick<
  z.infer<typeof avToolEingabeBasisSchema>,
  "status" | "krankenversicherung" | "hatKinder"
>

/** Sozialabgaben der Erwerbsphase, an den Beitragsbemessungsgrenzen gedeckelt. */
export function svBeitraegeArbeitnehmer(
  bruttoMonatlich: number,
  eingabe: SvEingabe
): { rv: number; av: number; kv: number; pv: number; summe: number } {
  const bemRv = Math.min(
    bruttoMonatlich,
    AV_ANNAHMEN.bbgRentenversicherung / 12
  )
  const bemKv = Math.min(
    bruttoMonatlich,
    AV_ANNAHMEN.bbgKrankenversicherung / 12
  )
  const pflegeZuschlag = eingabe.hatKinder
    ? 0
    : AV_ANNAHMEN.pflegeZuschlagKinderlose

  // Beamte sind versicherungsfrei in RV und AV; Selbstständige zahlen dort
  // nichts, dafür in der GKV den VOLLEN Satz statt des halben.
  const beitragspflichtig =
    eingabe.status === "arbeitnehmer" ||
    eingabe.status === "oeffentlicher_dienst"
  const rv = beitragspflichtig
    ? bemRv * AV_ANNAHMEN.rentenversicherungSatz * 0.5
    : 0
  const av = beitragspflichtig
    ? bemRv * AV_ANNAHMEN.arbeitslosenversicherungSatz * 0.5
    : 0

  let kv = 0
  let pv = 0
  if (eingabe.krankenversicherung.art === "pkv") {
    kv = eingabe.krankenversicherung.beitragMonatlich
    pv = 0 // im PKV-Beitrag enthalten
  } else {
    const zusatz = eingabe.krankenversicherung.zusatzbeitragProzent / 100
    const anteil = eingabe.status === "selbststaendig" ? 1 : 0.5
    kv = bemKv * (AV_ANNAHMEN.krankenversicherungSatz + zusatz) * anteil
    pv = bemKv * (AV_ANNAHMEN.pflegeversicherungSatz * anteil + pflegeZuschlag)
  }
  return { rv, av, kv, pv, summe: rv + av + kv + pv }
}

export type NettoAufstellung = {
  netto: number
  abzuege: {
    rentenversicherung: number
    arbeitslosenversicherung: number
    krankenversicherung: number
    pflegeversicherung: number
    lohnsteuer: number
    kirchensteuer: number
    summe: number
  }
}

type NettoEingabe = SvEingabe &
  Pick<
    z.infer<typeof avToolEingabeBasisSchema>,
    "kirchensteuer" | "steuerklasse"
  >

/** Block 4: Netto aus Brutto. */
export function nettoAusBrutto(
  bruttoMonatlich: number,
  eingabe: NettoEingabe
): NettoAufstellung {
  const sv = svBeitraegeArbeitnehmer(bruttoMonatlich, eingabe)
  const p = eingabe.steuerklasse
    ? STEUERKLASSEN_PARAMETER[eingabe.steuerklasse]
    : undefined
  // § 39b Abs. 2 Nr. 3 EStG, vereinfacht: RV-Anteil seit 2023 zu 100 %, KV und
  // PV in tatsächlicher Höhe, ohne Mindestansatz.
  const vorsorgepauschale = (sv.rv + sv.kv + sv.pv) * 12
  const pauschbetraege =
    !p || p.pauschbetraege
      ? AV_ANNAHMEN.arbeitnehmerPauschbetrag +
        AV_ANNAHMEN.sonderausgabenPauschbetrag
      : 0
  const zvE = Math.max(
    0,
    bruttoMonatlich * 12 - pauschbetraege - vorsorgepauschale
  )
  const lohnsteuer = lohnsteuerJahr(zvE, eingabe.steuerklasse) / 12
  const kirchensteuer = eingabe.kirchensteuer.pflichtig
    ? lohnsteuer *
      (eingabe.kirchensteuer.satz === "acht"
        ? AV_ANNAHMEN.kirchensteuerAcht
        : AV_ANNAHMEN.kirchensteuerNeun)
    : 0
  const summe = sv.summe + lohnsteuer + kirchensteuer
  return {
    netto: bruttoMonatlich - summe,
    abzuege: {
      rentenversicherung: sv.rv,
      arbeitslosenversicherung: sv.av,
      krankenversicherung: sv.kv,
      pflegeversicherung: sv.pv,
      lohnsteuer,
      kirchensteuer,
      summe,
    },
  }
}

/**
 * Block 4: Brutto aus Netto, per Bisektion.
 *
 * `nettoAusBrutto` steigt streng monoton im Brutto, ist aber stückweise
 * (BBG-Deckel, Tarifknicke) — eine geschlossene Umkehrung müsste bei jeder
 * verschobenen Grenze neu hergeleitet werden. Feste Schrittzahl statt
 * Konvergenzschleife, damit die Funktion deterministisch bleibt und die
 * Laufzeit gedeckelt ist; 60 Halbierungen sind weit jenseits der Cent-Grenze.
 */
export function bruttoAusNetto(
  nettoMonatlich: number,
  eingabe: NettoEingabe
): number {
  if (!(nettoMonatlich > 0)) return 0
  let unten = nettoMonatlich
  let oben = nettoMonatlich * 4 + 1000
  for (let i = 0; i < 60; i++) {
    const mitte = (unten + oben) / 2
    if (nettoAusBrutto(mitte, eingabe).netto < nettoMonatlich) unten = mitte
    else oben = mitte
  }
  return (unten + oben) / 2
}

/** Regelaltersgrenze nach § 235 SGB VI. Dieser Teil wird nicht genähert. */
export function regelaltersgrenze(geburtsjahr: number): number {
  if (geburtsjahr <= 1946) return 65
  if (geburtsjahr <= 1958) return 65 + (geburtsjahr - 1946) / 12
  if (geburtsjahr <= 1963) return 66 + ((geburtsjahr - 1958) * 2) / 12
  return 67
}

/** § 77 SGB VI. Nie negativ: ein Zugangsfaktor unter null hat keinen Sinn. */
export function zugangsfaktor(wunschalter: number, grenze: number): number {
  const monate = Math.round((wunschalter - grenze) * 12)
  if (monate === 0) return 1
  const faktor =
    monate < 0
      ? 1 + monate * AV_ANNAHMEN.abschlagProMonat
      : 1 + monate * AV_ANNAHMEN.zuschlagProMonat
  return Math.max(0, faktor)
}

/** § 22 Nr. 1 EStG: Besteuerungsanteil, ab Rentenbeginn 2058 volle 100 %. */
export function besteuerungsanteil(rentenjahr: number): number {
  const anteil =
    AV_ANNAHMEN.besteuerungsanteilBasis +
    (rentenjahr - AV_ANNAHMEN.besteuerungsanteilBasisJahr) *
      AV_ANNAHMEN.besteuerungsanteilSteigungProJahr
  return Math.min(1, Math.max(0, anteil))
}

/** Ertragsanteil nach dem Alter bei Rentenbeginn. */
export function ertragsanteil(rentenbeginnAlter: number): number {
  let anteil = ERTRAGSANTEIL_TABELLE[0]!.anteil
  for (const eintrag of ERTRAGSANTEIL_TABELLE)
    if (rentenbeginnAlter >= eintrag.abAlter) anteil = eintrag.anteil
  return anteil
}

/**
 * Block 5: Anwartschaft aus Beitrag, Laufzeit und Verzinsung.
 *
 * Monatliche Verzinsung, Beiträge am Periodenende — dieselbe Form wie der
 * Rentenfaktor in `personalPersonaFall`, damit AV-Tool und ausgelieferte
 * Trainingsfälle nicht auseinanderlaufen. Zinssatz null gesondert, sonst 0/0.
 *
 * Keine Kosten, keine Sterbetafel, ein Rentenfaktor für jedes Produkt: das
 * Werkzeug zielt auf die Größenordnung der Lücke, nicht auf ein Angebot.
 */
export function anwartschaftHochrechnung(args: {
  guthaben: number
  beitragMonatlich: number
  jahre: number
  zinsProzent: number
}): { kapital: number; monatsrente: number } {
  const i = args.zinsProzent / 100
  const n = Math.max(0, args.jahre) * 12
  const kapital =
    i === 0
      ? args.guthaben + args.beitragMonatlich * n
      : args.guthaben * (1 + i / 12) ** n +
        (args.beitragMonatlich * ((1 + i / 12) ** n - 1)) / (i / 12)
  return {
    kapital,
    monatsrente: (kapital * AV_ANNAHMEN.rentenfaktorJe10000) / 10_000,
  }
}

function aufloesen(berechnet: number, manuell?: number): UeberschriebenerWert {
  return manuell === undefined
    ? { berechnet, wert: berechnet, quelle: "berechnet" }
    : { berechnet, manuell, wert: manuell, quelle: "manuell" }
}

/* ------------------------------------------------------------------ *
 * Das Ganze
 * ------------------------------------------------------------------ */

/**
 * Rechnet die zehn Blöcke des AV-Tools durch.
 *
 * Reihenfolge ist nicht beliebig: die Inflationsannahme aus Block 10 geht nach
 * K3 in die Rentenbesteuerung von Block 9 ein, und die Nettorente aus Block 9
 * ist der Gegenwert, an dem Block 8 die Lücke misst.
 *
 * Verändert die Eingabe nicht.
 */
export function berechneAvTool(
  eingabe: z.infer<typeof avToolEingabeBasisSchema>
): AvToolErgebnis {
  const hinweise: string[] = []

  // --- Zeit (K2) ---
  const jahreBisRente = Math.max(
    0,
    eingabe.wunschEintrittsalter - eingabe.alter
  )
  const geburtsjahr = eingabe.basisjahr - eingabe.alter
  const rentenjahr = eingabe.basisjahr + jahreBisRente
  const grenze = regelaltersgrenze(geburtsjahr)
  const faktorZugang = zugangsfaktor(eingabe.wunschEintrittsalter, grenze)

  // --- Block 4: Einkommen ---
  const hochgerechnet = eingabe.einkommen.art === "netto"
  const bruttoMonatlich =
    eingabe.einkommen.art === "netto"
      ? bruttoAusNetto(eingabe.einkommen.nettoMonatlich, eingabe)
      : eingabe.einkommen.bruttoMonatlich
  const nettoAufstellung = nettoAusBrutto(bruttoMonatlich, eingabe)
  const nettoMonatlich = nettoAufstellung.netto

  // --- Block 10 zuerst, weil Block 9 daran hängt (K3) ---
  const inflation = eingabe.inflationProzent / 100
  const faktor = (1 + inflation) ** jahreBisRente
  const tarifFaktor = AV_ANNAHMEN.tarifAufRaedern ? faktor : 1

  // --- Block 6: gesetzliche Rente ---
  const punkteProJahr =
    Math.min(bruttoMonatlich * 12, AV_ANNAHMEN.bbgRentenversicherung) /
    AV_ANNAHMEN.durchschnittsentgelt
  const hatGrv =
    eingabe.status !== "beamter" &&
    (eingabe.status !== "selbststaendig" ||
      eingabe.gesetzlicheRente.beitragsjahreBisher > 0 ||
      eingabe.gesetzlicheRente.entgeltpunkteBisher !== undefined)
  const punkteBisher = hatGrv
    ? (eingabe.gesetzlicheRente.entgeltpunkteBisher ??
      eingabe.gesetzlicheRente.beitragsjahreBisher * punkteProJahr)
    : 0
  // Selbstständige zahlen nicht weiter ein; ihr Bestand bleibt stehen.
  const punkteKuenftig =
    hatGrv && eingabe.status !== "selbststaendig"
      ? jahreBisRente * punkteProJahr
      : 0
  const grvBerechnet =
    (punkteBisher + punkteKuenftig) * AV_ANNAHMEN.rentenwert * faktorZugang
  const grv = aufloesen(grvBerechnet, eingabe.gesetzlicheRente.ueberschreibung)

  // --- Block 7: Pflichtversorgung ---
  let versorgungsArt: "zvk" | "beamtenpension" | "keine" = "keine"
  let versorgungBerechnet = 0
  let ruhegehaltssatz: number | undefined
  let versorgungsabschlag: number | undefined
  if (eingabe.status === "oeffentlicher_dienst") {
    versorgungsArt = "zvk"
    versorgungBerechnet =
      bruttoMonatlich *
      AV_ANNAHMEN.zvkSatzProJahr *
      (eingabe.pflichtversorgung.jahre + jahreBisRente)
  } else if (eingabe.status === "beamter") {
    versorgungsArt = "beamtenpension"
    const dienstjahre = eingabe.pflichtversorgung.jahre + jahreBisRente
    ruhegehaltssatz = Math.min(
      AV_ANNAHMEN.ruhegehaltssatzMax,
      dienstjahre * AV_ANNAHMEN.ruhegehaltssatzProJahr
    )
    versorgungsabschlag = Math.min(
      AV_ANNAHMEN.versorgungsabschlagMax,
      Math.max(0, grenze - eingabe.wunschEintrittsalter) *
        AV_ANNAHMEN.versorgungsabschlagProJahr
    )
    versorgungBerechnet =
      bruttoMonatlich * ruhegehaltssatz * (1 - versorgungsabschlag)
  }
  const versorgung = aufloesen(
    versorgungBerechnet,
    eingabe.pflichtversorgung.ueberschreibung
  )

  // --- Block 5: bestehende Verträge ---
  const vertraege: AvVertragErgebnis[] = eingabe.vertraege.map((vertrag) => {
    const behandlung = VERTRAGS_BEHANDLUNG[vertrag.typ]
    let brutto: number
    let kapital: number | undefined
    if (vertrag.angabe.art === "rente_bekannt") {
      // K1: alles wird vor der Summierung auf nominal gebracht. Ohne diesen
      // Schritt addiert das Werkzeug zwei verschiedene Währungen.
      brutto =
        vertrag.angabe.wertbezug === "heute"
          ? vertrag.angabe.monatsrente * faktor
          : vertrag.angabe.monatsrente
    } else {
      const jahre = vertrag.angabe.laufzeitJahre ?? jahreBisRente
      if (jahre > jahreBisRente)
        hinweise.push(
          "Ein Vertrag läuft länger als bis zum Wunscheintrittsalter."
        )
      if (vertrag.angabe.zinsProzent > 6)
        hinweise.push(
          "Eine angenommene Verzinsung über 6 % ist sehr optimistisch."
        )
      const h = anwartschaftHochrechnung({
        guthaben: vertrag.angabe.guthaben,
        beitragMonatlich: vertrag.angabe.beitragMonatlich,
        jahre,
        zinsProzent: vertrag.angabe.zinsProzent,
      })
      brutto = h.monatsrente
      kapital = h.kapital
    }
    const steuerpflichtig =
      behandlung.besteuerung === "voll"
        ? brutto
        : behandlung.besteuerung === "kohorte"
          ? brutto * besteuerungsanteil(rentenjahr)
          : brutto * ertragsanteil(eingabe.wunschEintrittsalter)
    return {
      id: vertrag.id,
      typ: vertrag.typ,
      schicht: behandlung.schicht,
      hochgerechnet: vertrag.angabe.art === "hochrechnung",
      kapitalZuRentenbeginn: kapital === undefined ? undefined : cent(kapital),
      bruttoRenteMonatlich: cent(brutto),
      steuerpflichtigerAnteilMonatlich: cent(steuerpflichtig),
      kvPflichtigerAnteilMonatlich: behandlung.kvPflichtig ? cent(brutto) : 0,
    }
  })

  const summeSchicht = (schicht: Schicht) =>
    vertraege
      .filter((v) => v.schicht === schicht)
      .reduce((n, v) => n + v.bruttoRenteMonatlich, 0)

  const bruttoRenteGesamt =
    grv.wert +
    versorgung.wert +
    vertraege.reduce((n, v) => n + v.bruttoRenteMonatlich, 0)

  // --- Block 9: Nettorente ---
  const kasse = eingabe.krankenversicherung
  const zusatz = kasse.art === "gkv" ? kasse.zusatzbeitragProzent / 100 : 0
  const pflegeSatz =
    AV_ANNAHMEN.pflegeversicherungSatz +
    (eingabe.hatKinder ? 0 : AV_ANNAHMEN.pflegeZuschlagKinderlose)

  // Die Beitragspflicht ist je Quelle verschieden; eine Pauschale wäre hier
  // schlicht falsch. Versorgungsbezüge tragen den VOLLEN Satz, aber erst
  // oberhalb des Freibetrags.
  const bavPflichtig = vertraege.reduce(
    (n, v) => n + v.kvPflichtigerAnteilMonatlich,
    0
  )
  const bavUeberFreibetrag = Math.max(
    0,
    bavPflichtig - AV_ANNAHMEN.bavFreibetragMonatlich
  )
  const zvkRente = versorgungsArt === "zvk" ? versorgung.wert : 0

  let kvRente = 0
  let pvRente = 0
  let pkvBeitrag = 0
  let pkvZuschuss = 0
  if (kasse.art === "gkv") {
    kvRente =
      (grv.wert + zvkRente) *
        (AV_ANNAHMEN.krankenversicherungSatz / 2 + zusatz / 2) +
      bavUeberFreibetrag * (AV_ANNAHMEN.krankenversicherungSatz + zusatz)
    pvRente = (grv.wert + zvkRente + bavUeberFreibetrag) * pflegeSatz
  } else {
    const beitrag = kasse.beitragImRuhestandMonatlich ?? kasse.beitragMonatlich
    if (kasse.beitragImRuhestandMonatlich === undefined)
      hinweise.push(
        "Ohne eigenen Wert für den Ruhestand wird der heutige PKV-Beitrag " +
          "unverändert fortgeschrieben."
      )
    pkvZuschuss = Math.min(beitrag / 2, grv.wert * AV_ANNAHMEN.pkvZuschussSatz)
    pkvBeitrag = Math.max(0, beitrag - pkvZuschuss)
  }

  const anteilGesetzlich = besteuerungsanteil(rentenjahr)
  const steuerpflichtigeRente =
    (grv.wert + zvkRente) * anteilGesetzlich +
    (versorgungsArt === "beamtenpension" ? versorgung.wert : 0) +
    vertraege.reduce((n, v) => n + v.steuerpflichtigerAnteilMonatlich, 0)

  // § 10 Abs. 1 Nr. 3 EStG: Kranken- und Pflegebeiträge sind voll abziehbar.
  const vorsorgeJahr = (kvRente + pvRente + pkvBeitrag) * 12
  const grundfreibetragImRuhestand = AV_ANNAHMEN.grundfreibetrag * tarifFaktor
  const zvEJahr = Math.max(
    0,
    steuerpflichtigeRente * 12 -
      AV_ANNAHMEN.werbungskostenPauschbetragRente * tarifFaktor -
      AV_ANNAHMEN.sonderausgabenPauschbetrag * tarifFaktor -
      vorsorgeJahr
  )
  // In der Rentenphase gilt der Grundtarif: Steuerklassen sind ein Begriff der
  // Lohnsteuer und existieren für Renten nicht. Korrekt, aber überraschend.
  const estRente = einkommensteuerJahr(zvEJahr, tarifFaktor) / 12
  const kistRente = eingabe.kirchensteuer.pflichtig
    ? estRente *
      (eingabe.kirchensteuer.satz === "acht"
        ? AV_ANNAHMEN.kirchensteuerAcht
        : AV_ANNAHMEN.kirchensteuerNeun)
    : 0

  const abzuegeSumme =
    kvRente +
    pvRente +
    pkvBeitrag +
    estRente +
    kistRente +
    eingabe.sonstigeAbzuegeMonatlich
  const nettoRente = bruttoRenteGesamt - abzuegeSumme

  // --- Blöcke 8 und 10 ---
  const wunschrente =
    eingabe.wunschrente.art === "betrag"
      ? eingabe.wunschrente.betragMonatlich
      : (nettoMonatlich * eingabe.wunschrente.prozent) / 100
  const kaufkraftfaktor = 1 / faktor
  const nettoRenteKaufkraft = nettoRente * kaufkraftfaktor
  const lueckeHeute = Math.max(0, wunschrente - nettoRenteKaufkraft)

  if (eingabe.wunschEintrittsalter < 63 && hatGrv)
    hinweise.push(
      "Vor 63 gibt es keine gesetzliche Rente. Die Zeit bis dahin muss " +
        "anders überbrückt werden."
    )

  return {
    annahmenStand: AV_ANNAHMEN.stand,
    annahmenVersion: AV_ANNAHMEN.version,
    hinweis: AV_TOOL_HINWEIS,
    hinweise: [...new Set(hinweise)],

    zeit: {
      alter: eingabe.alter,
      wunschEintrittsalter: eingabe.wunschEintrittsalter,
      geburtsjahr,
      rentenjahr,
      jahreBisRente,
      regelaltersgrenze: grenze,
      zugangsfaktor: faktorZugang,
    },

    einkommen: {
      bruttoMonatlich: cent(bruttoMonatlich),
      nettoMonatlich: cent(nettoMonatlich),
      hochgerechnet,
      abzuege: {
        rentenversicherung: cent(nettoAufstellung.abzuege.rentenversicherung),
        arbeitslosenversicherung: cent(
          nettoAufstellung.abzuege.arbeitslosenversicherung
        ),
        krankenversicherung: cent(nettoAufstellung.abzuege.krankenversicherung),
        pflegeversicherung: cent(nettoAufstellung.abzuege.pflegeversicherung),
        lohnsteuer: cent(nettoAufstellung.abzuege.lohnsteuer),
        kirchensteuer: cent(nettoAufstellung.abzuege.kirchensteuer),
        summe: cent(nettoAufstellung.abzuege.summe),
      },
    },

    gesetzlicheRente: {
      bruttoMonatlich: {
        berechnet: cent(grv.berechnet),
        manuell: grv.manuell === undefined ? undefined : cent(grv.manuell),
        wert: cent(grv.wert),
        quelle: grv.quelle,
      },
      entgeltpunkteBisher: punkteBisher,
      entgeltpunkteKuenftig: punkteKuenftig,
      entgeltpunkteGesamt: punkteBisher + punkteKuenftig,
    },

    pflichtversorgung: {
      art: versorgungsArt,
      bruttoMonatlich: {
        berechnet: cent(versorgung.berechnet),
        manuell:
          versorgung.manuell === undefined
            ? undefined
            : cent(versorgung.manuell),
        wert: cent(versorgung.wert),
        quelle: versorgung.quelle,
      },
      ruhegehaltssatz,
      versorgungsabschlag,
    },

    vertraege,
    schichten: {
      ersteSchichtMonatlich: cent(summeSchicht("erste")),
      zweiteSchichtMonatlich: cent(summeSchicht("zweite")),
      dritteSchichtMonatlich: cent(summeSchicht("dritte")),
    },

    bruttoRenteGesamtMonatlich: cent(bruttoRenteGesamt),
    renteAbzuege: {
      krankenversicherung: cent(kvRente),
      pflegeversicherung: cent(pvRente),
      pkvBeitrag: cent(pkvBeitrag),
      pkvZuschuss: cent(pkvZuschuss),
      einkommensteuer: cent(estRente),
      kirchensteuer: cent(kistRente),
      sonstige: cent(eingabe.sonstigeAbzuegeMonatlich),
      // Als Differenz, nicht als eigene Summe: so gilt die Invariante
      // `netto + summe === brutto` auch nach der Cent-Rundung.
      summe: cent(bruttoRenteGesamt) - cent(nettoRente),
    },
    renteSteuer: {
      besteuerungsanteilGesetzlich: anteilGesetzlich,
      ertragsanteilPrivat: ertragsanteil(eingabe.wunschEintrittsalter),
      steuerpflichtigeRenteMonatlich: cent(steuerpflichtigeRente),
      abziehbareVorsorgeaufwendungenJahr: cent(vorsorgeJahr),
      zuVersteuerndesEinkommenJahr: cent(zvEJahr),
      grundfreibetragImRuhestand: cent(grundfreibetragImRuhestand),
    },
    nettoRenteMonatlich: cent(nettoRente),

    wunschrenteMonatlich: cent(wunschrente),
    wunschrenteNominalMonatlich: cent(wunschrente * faktor),
    inflationProzent: eingabe.inflationProzent,
    kaufkraftfaktor,
    nettoRenteKaufkraftMonatlich: cent(nettoRenteKaufkraft),
    kaufkraftverlustMonatlich: cent(nettoRente - nettoRenteKaufkraft),
    versorgungsluecke: {
      heuteMonatlich: cent(lueckeHeute),
      // Aus der heutigen Lücke hochgerechnet, nicht getrennt gerechnet: nur so
      // gilt die K1-Identität exakt und nicht nur ungefähr.
      nominalMonatlich: cent(lueckeHeute * faktor),
      ueberschussHeuteMonatlich: cent(
        Math.max(0, nettoRenteKaufkraft - wunschrente)
      ),
      deckungsgrad: wunschrente > 0 ? nettoRenteKaufkraft / wunschrente : 0,
    },
  }
}

/* ------------------------------------------------------------------ *
 * Was der Kunde zu sehen bekommt
 * ------------------------------------------------------------------ */

const EURO = new Intl.NumberFormat("de-DE", {
  style: "currency",
  currency: "EUR",
  maximumFractionDigits: 0,
})

/**
 * Why the result is not ready for the customer yet, or `null` when it is.
 *
 * Without income or a pension goal the tool computes zeros that still pass
 * the schema, and the customer would be shown "Wunschrente 0 €". Not a schema
 * rule on purpose: only showing is blocked, saving a half-filled tool must
 * keep working.
 */
export function avToolNochNichtZeigbar(ergebnis: AvToolErgebnis): string | null {
  if (ergebnis.einkommen.nettoMonatlich <= 0)
    return "Erfasse zuerst das Einkommen, dann kannst du das Ergebnis zeigen."
  if (ergebnis.wunschrenteMonatlich <= 0)
    return "Lege zuerst die Wunschrente fest, dann kannst du das Ergebnis zeigen."
  return null
}

/**
 * Der Satz, der im Verlauf steht, wenn die Beraterin „Ergebnis zeigen" drückt.
 *
 * Eine Regieanweisung aus Sicht des Kunden, kein Satz aus ihrem Mund: das
 * Erklären ist die trainierte Leistung, und die Rubrik „Werkzeug am Kunden
 * geführt" bewertet genau, ob sie die Zahl erklärt oder nur nennt. Ein Knopf,
 * der ihr den Satz formuliert, nähme ihr das ab — dieselbe Begründung, mit der
 * ARCHITEKTUR.md anklickbare Antwortvorschläge ablehnt.
 *
 * Hier und nicht im Browser, weil beide Wege ihn brauchen: der Textverlauf
 * schickt ihn als Zug, und der Server spielt ihn in ein laufendes
 * Sprachgespräch ein.
 */
export function avToolGezeigtText(ergebnis: AvToolErgebnis): string {
  const luecke = ergebnis.versorgungsluecke
  const ziel = EURO.format(ergebnis.wunschrenteMonatlich)
  if (luecke.heuteMonatlich > 0) {
    return (
      "Sie dreht den Bildschirm zu dir und zeigt dir die Auswertung: " +
      `Wunschrente ${ziel}, davon gedeckt ` +
      `${EURO.format(ergebnis.nettoRenteKaufkraftMonatlich)}. Es fehlen ` +
      `${EURO.format(luecke.heuteMonatlich)} im Monat, in heutiger Kaufkraft.`
    )
  }
  return (
    "Sie dreht den Bildschirm zu dir und zeigt dir die Auswertung: " +
    `Wunschrente ${ziel}, und deine Versorgung liegt mit ` +
    `${EURO.format(ergebnis.nettoRenteKaufkraftMonatlich)} darüber.`
  )
}
